(() => {
  const normalize = (value) => String(value ?? "").trim().toLowerCase();

  function optionIndex(config, name) {
    return config.options.findIndex((option) => normalize(option.name || option) === normalize(name));
  }

  function findVariant(config, bundle, delivery, otherOptions = []) {
    const bundleIndex = optionIndex(config, config.bundleOption);
    const deliveryIndex = optionIndex(config, config.deliveryOption);
    if (bundleIndex < 0 || (delivery === "preorder" && deliveryIndex < 0)) return null;
    const deliveryValue = delivery === "preorder" ? config.preorderValue : config.directValue;
    return config.variants.find((variant) => variant.options.every((value, index) => {
      if (index === bundleIndex) return normalize(value) === normalize(bundle);
      if (index === deliveryIndex) return normalize(value) === normalize(deliveryValue);
      return otherOptions[index] == null || value === otherOptions[index];
    })) || null;
  }

  function canBuy(config, variant, delivery) {
    return Boolean(variant?.available && !(delivery === "direct" && config.disableDirect));
  }

  function initialSelection(config, bundles) {
    const bundleIndex = optionIndex(config, config.bundleOption);
    const deliveryIndex = optionIndex(config, config.deliveryOption);
    const requested = config.variants.find((variant) => String(variant.id) === String(config.selectedVariantId));
    const preferredBundle = bundles.find((value) => normalize(value).includes("only")) || bundles[0];
    const bundle = requested?.options[bundleIndex] || preferredBundle;
    const otherOptions = requested?.options || config.variants[0]?.options || [];
    const requestedDelivery = deliveryIndex >= 0 && normalize(requested?.options[deliveryIndex]) === normalize(config.preorderValue)
      ? "preorder" : "direct";
    if (requested && !(config.disableDirect && requestedDelivery === "direct")) {
      return { bundle, delivery: requestedDelivery, otherOptions };
    }
    const deliveries = config.disableDirect ? ["preorder"] : ["direct", "preorder"];
    const delivery = deliveries.find((value) => canBuy(config, findVariant(config, bundle, value, otherOptions), value)) || deliveries[0];
    return { bundle, delivery, otherOptions };
  }

  function priceSummary(variant, directVariant, delivery) {
    if (!variant) return { price: null, compare: null, sale: 0, preorder: 0 };
    const isPreorder = delivery === "preorder";
    const regular = isPreorder && directVariant ? directVariant : variant;
    const compare = Math.max(variant.compare_at_price || 0, regular.compare_at_price || 0, isPreorder ? regular.price : 0);
    const preorder = isPreorder && directVariant ? Math.max(0, directVariant.price - variant.price) : 0;
    const sale = Math.max(0, compare - regular.price);
    return { price: variant.price, compare: compare > variant.price ? compare : null, sale, preorder };
  }

  function formatMarketMoney(cents, config, storeFormatter) {
    const losesFraction = cents % 100 !== 0 && config.moneyFormat?.includes("no_decimals");
    if (config.currency === config.shopCurrency && storeFormatter && !losesFraction) {
      return storeFormatter(cents, config.moneyFormat);
    }
    return new Intl.NumberFormat(config.locale, {
      style: "currency", currency: config.currency, maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  // The same resolver is exercised by the offline variant/price regression tests.
  if (typeof module !== "undefined" && module.exports) {
    module.exports = { findVariant, canBuy, initialSelection, priceSummary, formatMarketMoney };
  }
  if (typeof customElements === "undefined" || customElements.get("snuuzu-preorder-buybox")) return;

  class SnuuzuPreorderBuybox extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const source = this.querySelector("[data-preorder-config]");
      if (!source) return;
      this.config = JSON.parse(source.textContent);
      this.initialized = true;
      this.section = this.closest(".snuuzu-preorder-product");
      this.form = this.querySelector("product-form form");
      this.submit = this.form.querySelector("[type=submit]");
      this.sticky = this.querySelector("[data-sticky]");
      this.stickySubmit = this.querySelector("[data-sticky-submit]");
      this.bundleInputs = [...this.querySelectorAll("[data-bundle-group] input")];
      this.deliveryInputs = [...this.querySelectorAll("[data-delivery-group] input")];
      this.state = initialSelection(this.config, this.bundleInputs.map((input) => input.value));
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.addEventListener("change", (event) => this.onChange(event), options);
      // Stop invalid or stale selections before the theme's product-form handler runs.
      this.form.addEventListener("submit", (event) => {
        if (!canBuy(this.config, this.variant, this.state.delivery) || this.submit.disabled) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { ...options, capture: true });
      this.stickySubmit.addEventListener("click", () => {
        if (!this.stickySubmit.disabled) this.form.requestSubmit(this.submit);
      }, options);
      document.addEventListener("cart:error", (event) => {
        if (this.sticky.hidden || String(event.detail?.productVariantId) !== String(this.variant?.id)) return;
        // Keep the native cart error visible when a purchase starts from the sticky bar.
        this.querySelector(".snu-preorder__buy-buttons").scrollIntoView({ block: "center", behavior: "auto" });
      }, options);
      this.refresh(false);

      this.busyObserver = new MutationObserver(() => this.syncBusy());
      this.busyObserver.observe(this.submit, { attributes: true, attributeFilter: ["class", "disabled", "aria-disabled"] });
      this.scrollCheck = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateSticky();
        });
      };
      window.addEventListener("scroll", this.scrollCheck, { ...options, passive: true });
      window.addEventListener("resize", this.scrollCheck, options);
      this.updateSticky();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.busyObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.initialized = false;
    }

    money(cents) {
      // Preserve the market's symbol and fractional amount, including tax adjustments.
      const text = document.createElement("span");
      text.innerHTML = formatMarketMoney(cents, this.config, typeof formatMoney === "function" ? formatMoney : null);
      return text.textContent;
    }

    candidate(bundle = this.state.bundle, delivery = this.state.delivery) {
      return findVariant(this.config, bundle, delivery, this.state.otherOptions);
    }

    onChange(event) {
      if (event.target.matches("[data-bundle-group] input")) {
        this.state.bundle = event.target.value;
        if (!canBuy(this.config, this.candidate(), this.state.delivery)) {
          const alternative = this.state.delivery === "direct" ? "preorder" : "direct";
          if (canBuy(this.config, this.candidate(this.state.bundle, alternative), alternative)) this.state.delivery = alternative;
        }
      } else if (event.target.matches("[data-delivery-group] input")) {
        this.state.delivery = event.target.value;
      } else return;
      this.refresh(true);
    }

    setText(selector, value, hideEmpty = false) {
      this.querySelectorAll(selector).forEach((node) => {
        node.textContent = value;
        if (hideEmpty) node.hidden = !value;
      });
    }

    refresh(updateUrl) {
      this.variant = this.candidate();
      const direct = this.candidate(this.state.bundle, "direct");
      const preorder = this.candidate(this.state.bundle, "preorder");
      const amounts = priceSummary(this.variant, direct, this.state.delivery);
      const baseBundle = this.bundleInputs.find((input) => normalize(input.value).includes("only"))?.value || this.bundleInputs[0]?.value;
      const baseVariant = this.candidate(baseBundle);
      this.bundleInputs.forEach((input) => {
        input.checked = input.value === this.state.bundle;
        const candidate = this.candidate(input.value);
        const possible = ["direct", "preorder"].some((delivery) => canBuy(this.config, this.candidate(input.value, delivery), delivery));
        input.disabled = !possible;
        const extra = candidate && baseVariant ? candidate.price - baseVariant.price : 0;
        const price = input.closest(".snu-preorder__choice").querySelector("[data-bundle-price]");
        price.textContent = extra > 0 ? `+${this.money(extra)}` : "";
        price.hidden = extra <= 0;
      });
      this.deliveryInputs.forEach((input) => {
        input.checked = input.value === this.state.delivery;
        input.disabled = !canBuy(this.config, this.candidate(this.state.bundle, input.value), input.value);
      });
      this.setText("[data-direct-description]", canBuy(this.config, direct, "direct") ? this.config.directShipping : "Currently unavailable");
      this.setText("[data-preorder-description]", canBuy(this.config, preorder, "preorder") ? this.config.preorderShipping : "Currently unavailable");
      const savings = direct && preorder ? Math.max(0, direct.price - preorder.price) : 0;
      this.setText("[data-preorder-saving]", savings ? `Save ${this.money(savings)}` : "", true);
      const bundleLabel = this.bundleInputs.find((input) => input.value === this.state.bundle)?.dataset.label || "";
      const deliveryLabel = this.state.delivery === "preorder" ? "Pre-order" : "Direct delivery";
      this.setText("[data-selection]", [bundleLabel, deliveryLabel].filter(Boolean).join(" · "));
      this.setText("[data-current-price]", amounts.price == null ? this.config.unavailableLabel : this.money(amounts.price));
      this.setText("[data-compare-price]", amounts.compare ? this.money(amounts.compare) : "", true);
      const breakdown = [];
      if (amounts.sale) breakdown.push(`Sale −${this.money(amounts.sale)}`);
      if (amounts.preorder) breakdown.push(`Pre-order −${this.money(amounts.preorder)}`);
      this.setText("[data-discount-breakdown]", breakdown.join(" · "), true);
      this.setText("[data-why-saving]", savings ? `Save ${this.money(savings)} by ordering from our next production batch. ` : "");
      const available = canBuy(this.config, this.variant, this.state.delivery);
      const label = available ? (this.state.delivery === "preorder" ? this.config.preorderLabel : this.config.addLabel) : this.config.unavailableLabel;
      this.setText("[data-submit-label]", label);
      this.submit.disabled = !available;
      this.submit.setAttribute("aria-disabled", String(!available));
      this.form.elements.id.value = available ? this.variant.id : "";
      this.form.elements.id.disabled = !available;
      this.form.elements.quantity.value = this.variant?.quantity_rule?.min || 1;
      this.querySelector("[data-delivery-property]").value = deliveryLabel;
      this.querySelector("product-form")?.handleErrorMessage?.();
      const setupNote = this.querySelector("[data-setup-note]");
      if (setupNote) setupNote.hidden = optionIndex(this.config, this.config.deliveryOption) >= 0;
      if (updateUrl) {
        const url = new URL(window.location.href);
        if (this.variant) url.searchParams.set("variant", this.variant.id);
        else url.searchParams.delete("variant");
        window.history.replaceState({}, "", url);
        this.updateMedia();
      }
      this.syncBusy();
    }

    syncBusy() {
      const busy = this.submit.classList.contains("loading");
      this.querySelectorAll("fieldset").forEach((fieldset) => { fieldset.disabled = busy; });
      this.stickySubmit.disabled = busy || this.submit.disabled;
      this.stickySubmit.setAttribute("aria-busy", String(busy));
    }

    updateMedia() {
      const mediaId = this.variant?.featured_media?.id;
      if (!mediaId || !this.section) return;
      const mediaList = this.section.querySelector(".product__media-list");
      const slides = [...(mediaList?.querySelectorAll("[data-media-id]") || [])];
      const index = slides.findIndex((slide) => slide.dataset.mediaId === `${this.dataset.section}-${mediaId}`);
      if (index < 0) return;
      mediaList.swiper?.slideTo(index);
      this.section.querySelector(".product__media-sublist")?.swiper?.slideTo(index);
    }

    updateSticky() {
      // Below the buybox only: never cover the first gallery view or an open cart.
      const hasPassed = this.querySelector(".snu-preorder__buy-buttons").getBoundingClientRect().bottom <= 0;
      this.sticky.hidden = !hasPassed || !window.matchMedia("(max-width: 989px)").matches;
    }
  }
  customElements.define("snuuzu-preorder-buybox", SnuuzuPreorderBuybox);
})();
